import React, { useEffect, useState, useMemo } from "react";
import { DashboardLayout } from "../../components/DashboardLayout";
import { EmptyState } from "../../components/Common";
import { ActionPromptModal } from "../../components/ActionPromptModal";
import api from "../../lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Copy, Search, Download, ShieldAlert, ArrowLeft, Save } from "lucide-react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { usePermissions } from "../../lib/permissions";

// Shared layout helper — caller passes the role-specific sidebar `links` and `homeBase`
function withLayout(links, title, children) {
  return <DashboardLayout links={links} title={title}>{children}</DashboardLayout>;
}

// ---------- USERS LIST ----------
export function UsersPage({ links, homeBase }) {
  const [users, setUsers] = useState([]);
  const [q, setQ] = useState("");
  const [includeDeleted, setIncludeDeleted] = useState(false);
  const [roleFilter, setRoleFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [clinicFilter, setClinicFilter] = useState("");
  const [sortKey, setSortKey] = useState("created_at");
  const [sortDir, setSortDir] = useState("desc");
  const [selected, setSelected] = useState(new Set());
  const [confirmAction, setConfirmAction] = useState(null);
  const [busy, setBusy] = useState(false);
  const refresh = React.useCallback(
    () => api.get("/rbac/users", { params: { q, include_deleted: includeDeleted, ...(roleFilter ? { role: roleFilter } : {}), ...(homeBase?.includes("super-admin") && clinicFilter ? { clinic_id: clinicFilter } : {}) } }).then((r) => setUsers(r.data)),
    [q, includeDeleted, roleFilter, clinicFilter, homeBase]
  );
  useEffect(() => { refresh(); }, [refresh]);
  const nav = useNavigate();
  const isPlatform = homeBase?.includes("super-admin");

  const runRowAction = async (uid, action) => {
    try {
      if (action === "reset_password") {
        const r = await api.post(`/rbac/users/${uid}/reset-password`);
        toast.success(`Temp password: ${r.data.temp_password}`, { duration: 12000 });
      } else if (action === "resend_otp") {
        await api.post(`/rbac/users/${uid}/resend-otp`);
        toast.success("OTP resend queued");
      } else if (action === "resend_invite") {
        await api.post(`/rbac/users/${uid}/resend-invite`);
        toast.success("Invite resend queued");
      } else if (action === "delete") {
        await api.delete(`/rbac/users/${uid}`);
        toast.success("User deleted");
      } else if (action === "restore") {
        await api.post(`/rbac/users/${uid}/restore`);
        toast.success("Restored");
      } else if (action === "deactivate") {
        await api.patch(`/rbac/users/${uid}`, { status: "suspended" });
        toast.success("Deactivated");
      } else if (action === "activate") {
        await api.patch(`/rbac/users/${uid}`, { status: "active" });
        toast.success("Activated");
      }
      refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const runBulk = async (action) => {
    if (selected.size === 0) return;
    setBusy(true);
    try {
      const r = await api.post("/rbac/users/bulk", {
        user_ids: Array.from(selected), action,
        reason: confirmAction?.reason || "Bulk admin action",
      });
      const okN = r.data.ok?.length || 0;
      const failN = r.data.failed?.length || 0;
      if (action === "reset_password" && Object.keys(r.data.temp_passwords || {}).length) {
        const lines = Object.entries(r.data.temp_passwords).map(([uid, pw]) => `${uid.slice(-8)}: ${pw}`).join("\n");
        toast.success(`Passwords reset:\n${lines}`, { duration: 15000 });
      } else if (failN === 0) toast.success(`${okN} user(s) updated`);
      else toast.error(`${okN} ok, ${failN} failed (${r.data.failed?.[0]?.error || ""})`);
      setSelected(new Set());
      setConfirmAction(null);
      refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Bulk failed"); }
    setBusy(false);
  };

  const remove = (uid) => setConfirmAction({ kind: "single_delete", uid });
  const restore = (uid) => runRowAction(uid, "restore");

  const toggleSelect = (uid) => {
    setSelected((s) => {
      const n = new Set(s);
      if (n.has(uid)) n.delete(uid); else n.add(uid);
      return n;
    });
  };
  const toggleSort = (key) => {
    if (sortKey === key) setSortDir(sortDir === "asc" ? "desc" : "asc");
    else { setSortKey(key); setSortDir("asc"); }
  };
  const filtered = useMemo(() => users.filter((u) => !statusFilter || u.status === statusFilter || (statusFilter === "deleted" && u.deleted_at)),
    [users, statusFilter]);
  const sorted = useMemo(() => {
    const arr = [...filtered];
    arr.sort((a, b) => {
      const av = (a[sortKey] || "").toString().toLowerCase();
      const bv = (b[sortKey] || "").toString().toLowerCase();
      if (av < bv) return sortDir === "asc" ? -1 : 1;
      if (av > bv) return sortDir === "asc" ? 1 : -1;
      return 0;
    });
    return arr;
  }, [filtered, sortKey, sortDir]);
  const exportCsv = () => {
    if (!sorted.length) return toast.error("No data");
    const rows = sorted.map((u) => ({
      user_id: u.user_id, name: u.name, email: u.email, phone: u.phone || "",
      role: u.role, status: u.status, deleted_at: u.deleted_at || "",
      created_at: u.created_at, last_login_at: u.last_login_at || "",
    }));
    const headers = Object.keys(rows[0]);
    const esc = (v) => {
      const s = v === null || v === undefined ? "" : String(v);
      const safe = /^[=+\-@]/.test(s) ? `'${s}` : s;
      return /[",\n]/.test(safe) ? `"${safe.replace(/"/g, '""')}"` : safe;
    };
    const csv = [headers.join(","), ...rows.map((r) => headers.map((h) => esc(r[h])).join(","))].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `medigenie-users-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
  };

  return withLayout(links, isPlatform ? "Platform Users" : "Users", (
    <>
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative">
            <Search className="w-4 h-4 absolute top-3 left-3 text-mg-muted" />
            <input className="mg-input pl-9 max-w-xs" placeholder="Search users…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="users-search" />
          </div>
          <select className="mg-input text-sm max-w-xs" value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)} data-testid="users-role-filter">
            <option value="">All roles</option>
            <option value="super_admin">Super Admin</option>
            <option value="clinic_admin">Clinic Admin</option>
            <option value="doctor">Doctor</option>
            <option value="receptionist">Receptionist</option>
            <option value="patient">Patient</option>
          </select>
          <select className="mg-input text-sm max-w-xs" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} data-testid="users-status-filter">
            <option value="">All statuses</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
            <option value="suspended">Suspended</option>
            <option value="deleted">Deleted</option>
          </select>
          {isPlatform && <input className="mg-input text-sm max-w-xs" placeholder="Clinic ID" value={clinicFilter} onChange={(e) => setClinicFilter(e.target.value)} data-testid="users-clinic-filter" />}
          <label className="text-xs text-mg-secondary flex items-center gap-1.5">
            <input type="checkbox" checked={includeDeleted} onChange={(e) => setIncludeDeleted(e.target.checked)} data-testid="include-deleted" /> Show deleted
          </label>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={exportCsv} className="mg-btn-secondary text-sm" data-testid="export-users-csv">
            <Download className="w-4 h-4" /> CSV
          </button>
          <button onClick={() => nav(`${homeBase}/users/new`)} className="mg-btn-primary text-sm" data-testid="add-user-btn">
            <Plus className="w-4 h-4" /> Add user
          </button>
        </div>
      </div>

      {/* Bulk action bar */}
      {selected.size > 0 && (
        <div className="mg-card mb-3 bg-mg-medical/5 border-mg-medical/20" data-testid="bulk-user-bar">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="text-sm font-semibold text-mg-navy">{selected.size} selected</div>
            <div className="flex flex-wrap gap-1.5">
              <BulkBtn label="Activate" onClick={() => setConfirmAction({ kind: "bulk", action: "activate" })} testid="bulk-activate" />
              <BulkBtn label="Deactivate" onClick={() => setConfirmAction({ kind: "bulk", action: "deactivate" })} testid="bulk-deactivate" />
              <BulkBtn label="Reset password" onClick={() => setConfirmAction({ kind: "bulk", action: "reset_password" })} testid="bulk-reset-password" />
              <BulkBtn label="Resend OTP" onClick={() => setConfirmAction({ kind: "bulk", action: "resend_otp" })} testid="bulk-resend-otp" />
              <BulkBtn label="Resend invite" onClick={() => setConfirmAction({ kind: "bulk", action: "resend_invite" })} testid="bulk-resend-invite" />
              <BulkBtn label="Restore" onClick={() => setConfirmAction({ kind: "bulk", action: "restore" })} testid="bulk-restore" />
              <button onClick={() => setConfirmAction({ kind: "bulk", action: "soft_delete" })}
                className="text-xs px-3 py-1.5 rounded-lg border border-red-200 text-red-700 bg-red-50 hover:bg-red-100" data-testid="bulk-delete-users">
                <Trash2 className="w-3 h-3 inline mr-1" /> Delete
              </button>
              <button onClick={() => setSelected(new Set())} className="text-xs text-slate-500">Cancel</button>
            </div>
          </div>
        </div>
      )}

      <div className="mg-card">
        <table className="w-full text-sm" data-testid="users-table">
          <thead className="text-left text-xs uppercase tracking-wider text-mg-muted border-b border-mg-soft">
            <tr>
              <th className="w-8"><input type="checkbox" onChange={(e) => setSelected(e.target.checked ? new Set(sorted.map((u) => u.user_id)) : new Set())} checked={selected.size > 0 && selected.size === sorted.length} data-testid="select-all-users" /></th>
              <th onClick={() => toggleSort("name")} className="cursor-pointer hover:text-mg-medical select-none">Name {sortKey === "name" && (sortDir === "asc" ? "↑" : "↓")}</th>
              <th onClick={() => toggleSort("email")} className="cursor-pointer hover:text-mg-medical select-none">Email {sortKey === "email" && (sortDir === "asc" ? "↑" : "↓")}</th>
              <th>Phone</th>
              <th>Role</th>
              {isPlatform && <th>Clinics</th>}
              <th onClick={() => toggleSort("status")} className="cursor-pointer hover:text-mg-medical select-none">Status {sortKey === "status" && (sortDir === "asc" ? "↑" : "↓")}</th>
              <th>Verified</th>
              <th>Last login</th>
              <th onClick={() => toggleSort("created_at")} className="cursor-pointer hover:text-mg-medical select-none">Created {sortKey === "created_at" && (sortDir === "asc" ? "↑" : "↓")}</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((u) => (
              <tr key={u.user_id} className={`border-b border-slate-50 hover:bg-slate-50/40 ${u.deleted_at ? "opacity-50" : ""}`} data-testid={`user-row-${u.user_id}`}>
                <td><input type="checkbox" checked={selected.has(u.user_id)} onChange={() => toggleSelect(u.user_id)} data-testid={`select-${u.user_id}`} /></td>
                <td className="py-3 font-semibold text-mg-navy">{u.name}</td>
                <td className="text-xs">{u.email}</td>
                <td className="text-xs">{u.phone || "—"}</td>
                <td className="text-xs">{(u.assigned_roles || []).map((r) => r.name).join(", ") || u.role}</td>
                {isPlatform && <td className="text-xs">{(u.memberships || []).map((m) => m.clinic_name).join(", ") || "Platform only"}</td>}
                <td><span className={`mg-status mg-status-${u.deleted_at ? "cancelled" : u.status}`}>{u.deleted_at ? "deleted" : u.status}</span></td>
                <td className="text-xs">{u.email_verified ? "✓" : "—"}</td>
                <td className="text-xs text-mg-muted">{u.last_login_at ? new Date(u.last_login_at).toLocaleDateString() : "—"}</td>
                <td className="text-xs text-mg-muted">{u.created_at?.slice(0, 10)}</td>
                <td className="text-right">
                  <div className="inline-flex items-center gap-1">
                    {u.deleted_at ? (
                      <button onClick={() => restore(u.user_id)} className="text-xs text-mg-green font-semibold" data-testid={`restore-${u.user_id}`}>Restore</button>
                    ) : (
                      <>
                        <Link to={`${homeBase}/users/${u.user_id}`} className="text-xs text-mg-primary font-semibold mr-1" data-testid={`edit-${u.user_id}`}>Edit</Link>
                        <button onClick={() => runRowAction(u.user_id, "reset_password")} className="text-[10px] text-mg-medical font-semibold" data-testid={`reset-pw-${u.user_id}`}>Reset PW</button>
                        <button onClick={() => runRowAction(u.user_id, "resend_otp")} className="text-[10px] text-slate-600 ml-1" data-testid={`resend-otp-${u.user_id}`}>OTP</button>
                        <button onClick={() => runRowAction(u.user_id, "resend_invite")} className="text-[10px] text-slate-600 ml-1" data-testid={`resend-invite-${u.user_id}`}>Invite</button>
                        {u.status === "active" ? (
                          <button onClick={() => runRowAction(u.user_id, "deactivate")} className="text-[10px] text-mg-muted ml-1" data-testid={`deactivate-${u.user_id}`}>Deactivate</button>
                        ) : (
                          <button onClick={() => runRowAction(u.user_id, "activate")} className="text-[10px] text-mg-green ml-1" data-testid={`activate-${u.user_id}`}>Activate</button>
                        )}
                        <button onClick={() => remove(u.user_id)} className="text-[10px] text-red-500 ml-1" data-testid={`delete-user-${u.user_id}`}>
                          <Trash2 className="w-3.5 h-3.5 inline" />
                        </button>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {sorted.length === 0 && <tr><td colSpan="10" className="py-12"><EmptyState title="No users yet" message="Invite your team to manage the clinic." /></td></tr>}
          </tbody>
        </table>
      </div>

      {confirmAction && (
        <ConfirmModal
          action={confirmAction}
          count={selected.size}
          busy={busy}
          onCancel={() => setConfirmAction(null)}
          onConfirm={async (reason) => {
            if (confirmAction.kind === "single_delete") {
              try {
                await api.delete(`/rbac/users/${confirmAction.uid}`);
                toast.success("User deleted");
                setConfirmAction(null);
                refresh();
              } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
            } else {
              await runBulk(confirmAction.action);
            }
          }}
        />
      )}
    </>
  ));
}

function BulkBtn({ label, onClick, testid }) {
  return <button onClick={onClick} className="text-xs px-3 py-1.5 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 text-slate-700" data-testid={testid}>{label}</button>;
}

function ConfirmModal({ action, count, onCancel, onConfirm, busy }) {
  const [reason, setReason] = useState("");
  const titles = {
    activate: "Activate users", deactivate: "Deactivate users",
    reset_password: "Reset passwords (auto-generated)",
    resend_otp: "Resend OTP", resend_invite: "Resend invite",
    soft_delete: "Soft-delete users", restore: "Restore users",
  };
  const destructive = ["soft_delete", "deactivate"].includes(action.action);
  const isSingleDel = action.kind === "single_delete";
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl p-6 max-w-md w-full">
        <h3 className="font-bold text-mg-navy text-lg">
          {isSingleDel ? "Soft-delete user" : titles[action.action]}
        </h3>
        {!isSingleDel && (
          <p className="text-sm text-slate-600 mt-1">This affects <strong>{count}</strong> user(s).</p>
        )}
        {destructive && (
          <p className="text-xs text-red-600 mt-1">Last super admin protection is enforced. Self-action is rejected.</p>
        )}
        <label className="block text-xs uppercase tracking-wider font-bold text-slate-500 mt-4">Reason</label>
        <input value={reason} onChange={(e) => setReason(e.target.value)} className="mg-input mt-1 w-full" data-testid="confirm-reason" placeholder="Audit-logged with this reason…" />
        <div className="flex gap-2 mt-5">
          <button onClick={onCancel} className="mg-btn-secondary flex-1 justify-center" data-testid="confirm-cancel">Cancel</button>
          <button onClick={() => onConfirm(reason)} disabled={busy}
            className={`flex-1 justify-center ${destructive ? "text-sm px-3 py-1.5 rounded-lg border border-red-200 text-white bg-red-600 hover:bg-red-700" : "mg-btn-primary text-sm"}`}
            data-testid="confirm-action">{busy ? "Working…" : "Confirm"}</button>
        </div>
      </div>
    </div>
  );
}

// ---------- USER FORM (CREATE/EDIT) ----------
export function UserFormPage({ links, homeBase, mode = "create" }) {
  const nav = useNavigate();
  const { id: userId } = useParams();
  const [form, setForm] = useState({ name: "", email: "", phone: "", password: "", role_ids: [], role_slug_fallback: "receptionist" });
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(false);
  const isPlatform = homeBase?.includes("super-admin");
  useEffect(() => {
    api.get("/rbac/roles").then((r) => setRoles(r.data));
    if (mode === "edit") {
      api.get(`/rbac/users/${userId}`).then((r) => {
        setForm({
          name: r.data.user.name, email: r.data.user.email,
          phone: r.data.user.phone || "", password: "",
          role_ids: r.data.assigned_roles.map((x) => x.role_id),
          role_slug_fallback: r.data.user.role,
        });
      });
    }
  }, [mode, userId]);
  const submit = async () => {
    setLoading(true);
    try {
      if (mode === "edit") {
        await api.patch(`/rbac/users/${userId}`, { name: form.name, phone: form.phone, role_ids: form.role_ids });
        toast.success("User updated");
      } else {
        await api.post("/rbac/users", form);
        toast.success("User created");
      }
      nav(`${homeBase}/users`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setLoading(false); }
  };
  return withLayout(links, mode === "edit" ? "Edit user" : "Add user", (
    <>
      <Link to={`${homeBase}/users`} className="text-sm text-mg-primary mb-4 inline-flex items-center gap-1"><ArrowLeft className="w-4 h-4" /> Back to users</Link>
      <div className="mg-card max-w-2xl">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <input className="mg-input" placeholder="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="user-name" />
          <input className="mg-input" placeholder="Email" type="email" value={form.email} disabled={mode === "edit"} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="user-email" />
          <input className="mg-input" placeholder="Phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
          {mode === "create" && (
            <input className="mg-input" placeholder="Initial password (optional; auto-generated if blank)" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
          )}
        </div>
        <div className="mt-4">
          <label className="text-xs uppercase tracking-wider text-mg-muted font-semibold">Assigned roles</label>
          <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2">
            {roles.filter((r) => isPlatform || r.slug !== "clinic_admin").map((r) => (
              <label key={r.role_id} className="flex items-center gap-2 text-sm p-2 rounded-lg hover:bg-slate-50">
                <input type="checkbox" checked={form.role_ids.includes(r.role_id)} onChange={(e) => {
                  setForm((f) => ({
                    ...f, role_ids: e.target.checked ? [...f.role_ids, r.role_id] : f.role_ids.filter((x) => x !== r.role_id),
                  }));
                }} data-testid={`role-checkbox-${r.role_id}`} />
                <span className="font-semibold text-mg-navy">{r.name}</span>
                <span className="text-xs text-mg-muted">({r.permission_count} perms)</span>
                {r.is_system_role && <span className="text-[10px] bg-blue-50 text-mg-primary px-1.5 py-0.5 rounded-full">System</span>}
              </label>
            ))}
          </div>
        </div>
        <button onClick={submit} disabled={loading} className="mg-btn-primary mt-5" data-testid="save-user-btn">
          <Save className="w-4 h-4" /> {loading ? "Saving…" : "Save user"}
        </button>
      </div>
    </>
  ));
}

// ---------- ROLES LIST ----------
export function RolesPage({ links, homeBase }) {
  const [roles, setRoles] = useState([]);
  const [cloneModal, setCloneModal] = useState(null);
  const [cloneBusy, setCloneBusy] = useState(false);
  const nav = useNavigate();
  const refresh = () => api.get("/rbac/roles").then((r) => setRoles(r.data));
  useEffect(() => { refresh(); }, []);
  const remove = async (id, name) => {
    if (!window.confirm(`Delete role "${name}"? Users assigned to it will lose this role.`)) return;
    try { await api.delete(`/rbac/roles/${id}`); toast.success("Role deleted"); refresh(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const resetToDefault = async (id, name) => {
    if (!window.confirm(`Reset "${name}" back to the platform default? Your clinic's customizations will be removed.`)) return;
    try { await api.delete(`/rbac/roles/${id}`); toast.success("Reverted to platform default"); refresh(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const clone = async (id, name) => {
    setCloneModal({ id, name });
  };
  const submitClone = async (newName) => {
    if (!cloneModal) return;
    const trimmedName = newName.trim();
    if (!trimmedName) {
      toast.error("Role name is required");
      return;
    }
    setCloneBusy(true);
    try {
      await api.post("/rbac/roles/clone", { source_role_id: cloneModal.id, new_name: trimmedName });
      toast.success("Cloned");
      setCloneModal(null);
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
    setCloneBusy(false);
  };
  return withLayout(links, "Roles & Permissions", (
    <>
      <div className="flex items-center justify-end mb-4">
        <button onClick={() => nav(`${homeBase}/roles/new`)} className="mg-btn-primary text-sm" data-testid="add-role-btn">
          <Plus className="w-4 h-4" /> Create custom role
        </button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {roles.map((r) => {
          const isSuperAdminRole = r.slug === "super_admin";
          const isClinicOverride = r.is_system_role && !!r.clinic_id;
          return (
            <div key={r.role_id} className="mg-card" data-testid={`role-card-${r.role_id}`}>
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="font-bold text-mg-navy">{r.name}</h3>
                  <p className="text-xs text-mg-secondary mt-0.5">{r.description}</p>
                </div>
                <div className="flex flex-col gap-1 items-end">
                  {r.is_system_role && <span className="text-[10px] bg-blue-50 text-mg-primary px-2 py-0.5 rounded-full font-bold">SYSTEM</span>}
                  {isClinicOverride && <span className="text-[10px] bg-amber-50 text-amber-700 px-2 py-0.5 rounded-full font-bold">CUSTOMIZED</span>}
                </div>
              </div>
              <div className="flex gap-3 mt-3 text-xs text-mg-secondary">
                <span><strong className="text-mg-navy">{r.permission_count}</strong> permissions</span>
                <span><strong className="text-mg-navy">{r.user_count}</strong> users</span>
              </div>
              <div className="flex gap-2 mt-4">
                <button onClick={() => clone(r.role_id, r.name)} className="text-xs text-mg-primary font-semibold flex items-center gap-1" data-testid={`clone-role-${r.role_id}`}>
                  <Copy className="w-3 h-3" /> Clone
                </button>
                {isSuperAdminRole ? (
                  <Link to={`${homeBase}/roles/${r.role_id}`} className="text-xs text-mg-primary font-semibold ml-auto" data-testid={`view-role-${r.role_id}`}>View →</Link>
                ) : (
                  <>
                    <Link to={`${homeBase}/roles/${r.role_id}`} className="text-xs text-mg-primary font-semibold ml-auto" data-testid={`edit-role-${r.role_id}`}>Edit →</Link>
                    {isClinicOverride ? (
                      <button onClick={() => resetToDefault(r.role_id, r.name)} className="text-[10px] text-amber-600 font-semibold" data-testid={`reset-role-${r.role_id}`}>Reset to default</button>
                    ) : !r.is_system_role && (
                      <button onClick={() => remove(r.role_id, r.name)} className="text-xs text-red-500" data-testid={`delete-role-${r.role_id}`} aria-label={`Delete ${r.name} role`}><Trash2 className="w-3.5 h-3.5" aria-hidden="true" /></button>
                    )}
                  </>
                )}
              </div>
            </div>
          );
        })}
      </div>
      <ActionPromptModal
        open={Boolean(cloneModal)}
        title={cloneModal ? `Clone "${cloneModal.name}" as` : "Clone role"}
        label="New role name"
        placeholder={cloneModal ? `${cloneModal.name} (Copy)` : ""}
        initialValue={cloneModal ? `${cloneModal.name} (Copy)` : ""}
        confirmLabel="Clone"
        multiline={false}
        busy={cloneBusy}
        onCancel={() => setCloneModal(null)}
        onConfirm={submitClone}
      />
    </>
  ));
}

// ---------- ROLE FORM / PERMISSION MATRIX ----------
export function RoleFormPage({ links, homeBase, mode = "create" }) {
  const { refresh: refreshEffectivePermissions } = usePermissions();
  const nav = useNavigate();
  const { id: roleId } = useParams();
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [permissions, setPermissions] = useState([]); // selected slugs
  const [catalog, setCatalog] = useState([]);
  const [effective, setEffective] = useState([]);
  const [sensitive, setSensitive] = useState([]);
  const [readOnly, setReadOnly] = useState(false);
  const [isSystemRole, setIsSystemRole] = useState(false);
  const [isClinicOverride, setIsClinicOverride] = useState(false);
  const [saving, setSaving] = useState(false);
  const isSuperAdminUser = homeBase?.includes("super-admin");

  useEffect(() => {
    api.get("/rbac/permissions").then((r) => {
      setCatalog(r.data.permissions);
      setEffective(r.data.effective);
      setSensitive(r.data.sensitive_slugs);
    });
    if (mode === "edit" && roleId) {
      api.get(`/rbac/roles/${roleId}`).then((r) => {
        setName(r.data.name);
        setDescription(r.data.description || "");
        setPermissions(r.data.permissions || []);
        setIsSystemRole(!!r.data.is_system_role);
        setIsClinicOverride(!!(r.data.is_system_role && r.data.clinic_id));
        // Only the Super Admin role stays permanently locked. All other
        // system roles are editable — for a clinic user this creates a
        // clinic-specific customized copy; for Super Admin it edits the
        // platform-wide default template.
        setReadOnly(r.data.slug === "super_admin" || (r.data.slug === "clinic_admin" && !isSuperAdminUser));
      });
    }
  }, [mode, roleId, isSuperAdminUser]);

  const byModule = useMemo(() => {
    const m = {};
    catalog.forEach((p) => { (m[p.module] = m[p.module] || []).push(p); });
    return m;
  }, [catalog]);

  const toggle = (slug) => {
    if (readOnly) return;
    if (!effective.includes(slug) && !permissions.includes(slug)) {
      toast.error("You don't have this permission yourself — cannot grant it.");
      return;
    }
    setPermissions((p) => p.includes(slug) ? p.filter((x) => x !== slug) : [...p, slug]);
  };
  const toggleModuleAll = (modKey, state) => {
    if (readOnly) return;
    const modSlugs = byModule[modKey].map((p) => p.slug).filter((s) => effective.includes(s));
    setPermissions((p) => {
      const set = new Set(p);
      modSlugs.forEach((s) => state ? set.add(s) : set.delete(s));
      return Array.from(set);
    });
  };
  const submit = async () => {
    const grantingSensitive = permissions.filter((p) => sensitive.includes(p));
    if (grantingSensitive.length > 0) {
      if (!window.confirm(`You are granting ${grantingSensitive.length} sensitive permission(s): ${grantingSensitive.slice(0, 6).join(", ")}${grantingSensitive.length > 6 ? "…" : ""}. Continue?`)) return;
    }
    setSaving(true);
    try {
      if (mode === "edit") {
        const r = await api.patch(`/rbac/roles/${roleId}`, { name, description, permissions, role_type: "clinic" });
        if (r.data.scope === "clinic_override") {
          toast.success("Saved — a customized copy of this role was created for your clinic only.");
        } else if (r.data.scope === "platform_default") {
          toast.success("Saved — updated the platform-wide default for clinics that haven't customized this role.");
        } else {
          toast.success("Saved");
        }
      } else {
        await api.post("/rbac/roles", { name, description, permissions, role_type: "clinic" });
        toast.success("Saved");
      }
      await refreshEffectivePermissions();
      nav(`${homeBase}/roles`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  return withLayout(links, mode === "edit" ? "Edit role" : "Create role", (
    <>
      <Link to={`${homeBase}/roles`} className="text-sm text-mg-primary mb-4 inline-flex items-center gap-1"><ArrowLeft className="w-4 h-4" /> Back to roles</Link>
      <div className="mg-card mb-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <input className="mg-input" placeholder="Role name (e.g. Senior Nurse)" value={name} onChange={(e) => setName(e.target.value)} disabled={readOnly || isSystemRole} data-testid="role-name" />
          <input className="mg-input" placeholder="Description" value={description} onChange={(e) => setDescription(e.target.value)} disabled={readOnly} data-testid="role-description" />
        </div>
        {readOnly && (
          <div className="mt-3 p-3 bg-amber-50 text-amber-900 text-xs rounded-lg flex items-center gap-2" data-testid="role-readonly-banner">
            <ShieldAlert className="w-4 h-4" /> The Super Admin role has full platform access and can never be edited.
          </div>
        )}
        {!readOnly && isSystemRole && !isClinicOverride && (
          <div className="mt-3 p-3 bg-blue-50 text-mg-primary text-xs rounded-lg flex items-center gap-2" data-testid="role-override-banner">
            <ShieldAlert className="w-4 h-4" />
            {isSuperAdminUser
              ? "This is the platform default. Saving updates it for every clinic that hasn't customized this role themselves."
              : "This is a shared system role. Saving will create a customized copy for your clinic only — other clinics keep the original default."}
          </div>
        )}
        {!readOnly && isClinicOverride && (
          <div className="mt-3 p-3 bg-amber-50 text-amber-900 text-xs rounded-lg flex items-center gap-2" data-testid="role-customized-banner">
            <ShieldAlert className="w-4 h-4" /> This role is already customized for your clinic. Changes apply only to your clinic's staff.
          </div>
        )}
      </div>
      <div className="mg-card overflow-x-auto">
        <h3 className="font-bold text-mg-navy mb-3">Permission matrix ({permissions.length} selected)</h3>
        <table className="w-full text-sm">
          <thead className="text-left text-xs uppercase tracking-wider text-mg-muted border-b border-mg-soft">
            <tr><th className="py-2 w-48">Module</th><th>Permissions</th><th className="text-right">All</th></tr>
          </thead>
          <tbody>
            {Object.keys(byModule).sort().map((modKey) => {
              const modPerms = byModule[modKey];
              const allSelected = modPerms.every((p) => permissions.includes(p.slug));
              return (
                <tr key={modKey} className="border-b border-slate-50 align-top">
                  <td className="py-3 font-semibold text-mg-navy capitalize">{modKey.replace(/_/g, " ")}</td>
                  <td className="py-3">
                    <div className="flex flex-wrap gap-1.5">
                      {modPerms.map((p) => {
                        const checked = permissions.includes(p.slug);
                        const canGrant = effective.includes(p.slug);
                        return (
                          <button
                            key={p.slug}
                            onClick={() => toggle(p.slug)}
                            disabled={readOnly || !canGrant}
                            title={p.description + (p.is_sensitive ? " (sensitive)" : "")}
                            className={`text-xs px-2.5 py-1 rounded-full border transition-all ${
                              checked
                                ? p.is_sensitive
                                  ? "bg-amber-50 border-amber-300 text-amber-900 font-semibold"
                                  : "bg-blue-50 border-blue-300 text-mg-primary font-semibold"
                                : canGrant
                                  ? "bg-white border-slate-200 text-mg-secondary hover:border-mg-primary"
                                  : "bg-slate-50 border-slate-100 text-mg-muted opacity-60 cursor-not-allowed"
                            }`}
                            data-testid={`perm-${p.slug}`}
                          >
                            {p.action}{p.is_sensitive && " ⚠"}
                          </button>
                        );
                      })}
                    </div>
                  </td>
                  <td className="text-right">
                    <input type="checkbox" checked={allSelected} onChange={(e) => toggleModuleAll(modKey, e.target.checked)} disabled={readOnly} data-testid={`module-all-${modKey}`} />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {!readOnly && (
          <div className="mt-5 flex justify-end gap-2">
            <Link to={`${homeBase}/roles`} className="mg-btn-secondary text-sm">Cancel</Link>
            <button onClick={submit} disabled={saving || !name} className="mg-btn-primary text-sm" data-testid="save-role-btn">
              <Save className="w-4 h-4" /> {saving ? "Saving…" : "Save role"}
            </button>
          </div>
        )}
      </div>
    </>
  ));
}
